import React, { useCallback, useEffect, useState } from 'react';
import './NotificationsPanel.css';
import './HelpRequestsPanel.css';

const STATUS_FILTERS = ['Open', 'Resolved', 'All'];

function HelpRequestsPanel({ authToken, onAuthExpired, showAlert, showConfirm }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('Open');

  const request = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { ...options.headers, Authorization: `Bearer ${authToken}` },
    });
    if (response.status === 401 || response.status === 403) {
      showAlert('error', 'Session expired. Please login again.', onAuthExpired);
      return null;
    }
    return response;
  }, [authToken, onAuthExpired, showAlert]);

  const fetchRequests = useCallback(async () => {
    if (!authToken) return;
    try {
      setLoading(true);
      const response = await request('/api/support/requests');
      if (!response) return;
      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching help requests:', error);
      showAlert('error', 'Failed to load help requests');
    } finally {
      setLoading(false);
    }
  }, [authToken, request, showAlert]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const setStatus = async (helpRequest, status) => {
    try {
      const response = await request(`/api/support/requests/${helpRequest.Id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: status }),
      });
      if (!response) return;
      if (!response.ok) {
        showAlert('error', 'Failed to update help request');
        return;
      }
      fetchRequests();
    } catch (error) {
      console.error('Error updating help request:', error);
      showAlert('error', 'Failed to update help request');
    }
  };

  const handleDelete = (helpRequest) => {
    showConfirm(`Delete the help request from ${helpRequest.Email}?`, async () => {
      try {
        const response = await request(`/api/support/requests/${helpRequest.Id}`, { method: 'DELETE' });
        if (!response) return;
        if (!response.ok) {
          showAlert('error', 'Failed to delete help request');
          return;
        }
        showAlert('success', 'Help request deleted');
        fetchRequests();
      } catch (error) {
        console.error('Error deleting help request:', error);
        showAlert('error', 'Failed to delete help request');
      }
    });
  };

  const visible = statusFilter === 'All' ? requests : requests.filter((r) => r.Status === statusFilter);
  const openCount = requests.filter((r) => r.Status === 'Open').length;

  return (
    <section className="notifications-panel help-requests-panel">
      <div className="notifications-header">
        <div>
          <h2>Help Requests {openCount > 0 && <span className="help-open-count">{openCount} open</span>}</h2>
          <p>Messages sent from the TreeWorks add-in&apos;s Get Help button.</p>
        </div>
        <div className="notifications-actions">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>{status === 'All' ? 'All requests' : status}</option>
            ))}
          </select>
          <button className="btn btn-secondary" onClick={fetchRequests}>Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading help requests...</div>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <p>{requests.length === 0 ? 'No help requests yet.' : 'No help requests match this filter.'}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="licenses-table help-requests-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>From</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((helpRequest) => (
                <tr key={helpRequest.Id}>
                  <td data-label="Received">
                    {helpRequest.CreatedAt ? new Date(helpRequest.CreatedAt).toLocaleString() : 'N/A'}
                  </td>
                  <td data-label="From">
                    <div className="help-email">{helpRequest.Email}</div>
                    <div className="help-meta">
                      {helpRequest.CompanyName || 'Unlicensed / unknown license'}
                      {helpRequest.AppVersion && ` · v${helpRequest.AppVersion}`}
                    </div>
                    {helpRequest.LicenseKey && <div className="help-meta help-license">{helpRequest.LicenseKey}</div>}
                  </td>
                  <td className="help-message" data-label="Message">{helpRequest.Message}</td>
                  <td data-label="Status">
                    <span className={`status-badge ${helpRequest.Status === 'Open' ? 'status-warning' : 'status-active'}`}>
                      {helpRequest.Status}
                    </span>
                  </td>
                  <td className="actions" data-label="Actions">
                    <button
                      className="action-btn toggle-btn"
                      onClick={() => setStatus(helpRequest, helpRequest.Status === 'Open' ? 'Resolved' : 'Open')}
                    >
                      {helpRequest.Status === 'Open' ? 'Mark Resolved' : 'Reopen'}
                    </button>
                    <button className="action-btn delete-btn" onClick={() => handleDelete(helpRequest)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default HelpRequestsPanel;
